import React, { useState, useRef } from 'react';
import { Shield } from 'lucide-react';

interface ConfidenceBadgeProps {
  score: number;
  uncertaintyReasons?: string[];
}

function getConfidenceColor(score: number) {
  if (score >= 0.8) return { bg: 'bg-green-100', text: 'text-green-700', icon: 'text-green-500' };
  if (score >= 0.6) return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'text-yellow-500' };
  return { bg: 'bg-red-100', text: 'text-red-700', icon: 'text-red-500' };
}

function getConfidenceLabel(score: number): string {
  if (score >= 0.8) return 'High';
  if (score >= 0.6) return 'Medium';
  return 'Low';
}

export function ConfidenceBadge({ score, uncertaintyReasons }: ConfidenceBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const colors = getConfidenceColor(score);
  const label = getConfidenceLabel(score);
  const pct = Math.round(score * 100);

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: Math.max(8, rect.right - 256),
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="inline-block" data-testid="confidence-badge">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${colors.bg} ${colors.text} hover:opacity-80 transition-opacity`}
        title={`${label} confidence (${pct}%)`}
      >
        <Shield className={`h-2.5 w-2.5 ${colors.icon}`} />
        {pct}%
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
          <div
            className="fixed z-[101] w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700"
            style={{ top: position.top, left: position.left, transform: 'translateY(-100%)' }}
            data-testid="confidence-tooltip"
          >
            <div className="font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
              <Shield className={`h-3.5 w-3.5 ${colors.icon}`} />
              {label} Confidence — {pct}%
            </div>

            {uncertaintyReasons && uncertaintyReasons.length > 0 ? (
              <div>
                <div className="text-gray-500 mb-1">Uncertainty reasons:</div>
                <ul className="space-y-1">
                  {uncertaintyReasons.map((reason, idx) => (
                    <li key={idx} className="flex items-start gap-1.5">
                      <span className="text-yellow-500 mt-0.5 flex-shrink-0">⚠</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-gray-500">
                No uncertainty factors detected.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
